import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import layoutStyles from './layout.module.scss';
import PageTransition from './components/PageTransition';
import { useDispatch } from 'react-redux';
import { createUseStyles } from 'react-jss';
import PerfectScrollbar from 'perfect-scrollbar';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { logout } from './store/actions/auth';
import Bugsnag from './utils/bugsnag';
import { AppRoutes } from './config';

import NotFound from './views/NotFound';
import ServerError from './views/ServerError';
import styles from './assets/jss/material-dashboard-pro-react/layouts/adminStyle.js';
import 'perfect-scrollbar/css/perfect-scrollbar.css';

import IdleTimer from './components/IdleTimer/IdleTimer';
import AdminNavbar from './components/Navbars/AdminNavbar.js';
import Sidebar from './components/Sidebar/Sidebar.js';
import menus from './config/menus.js';
import { clearLocalStoragePreservingPrefs } from './utils/clearLocalStoragePreservingPrefs';
import { buildLoginPath } from './utils';

var ps;
const ErrorBoundary = Bugsnag.getPlugin('react');
const useStyles = createUseStyles(styles);

export default function Layout(props) {
    const { ...rest } = props;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const classes = useStyles();
    const { t } = useTranslation();

    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [miniActive, setMiniActive] = React.useState(() => {
        return window.innerWidth >= 600 && window.innerWidth < 900;
    });
    const userCollapsedRef = useRef(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [justLoggedIn, setJustLoggedIn] = useState(() => {
        const flag = sessionStorage.getItem('justLoggedIn');
        if (flag) {
            sessionStorage.removeItem('justLoggedIn');
            return true;
        }
        return false;
    });

    const handleLogoutStart = () => {
        setLoggingOut(true);
        setTimeout(() => {
            dispatch(logout(navigate));
        }, 420);
    };
    // styles
    const mainPanelClasses =
        classes.mainPanel +
        ' ' +
        cx({
            [classes.mainPanelSidebarMini]: miniActive,
            [classes.mainPanelWithPerfectScrollbar]: navigator.platform.indexOf('Win') > -1,
        });

    if (!localStorage.getItem('user')) {
        clearLocalStoragePreservingPrefs();
        window.location.href = buildLoginPath();
        return null;
    }

    // ref for main panel div
    const mainPanel = React.createRef();

    useEffect(() => {
        const timer = new IdleTimer({
            timeout: 3600,
            onTimeout: () => {
                dispatch(logout(navigate, { skipServerCall: true }));
            },
            onExpired: () => {
                dispatch(logout(navigate, { skipServerCall: true }));
            },
        });

        return () => {
            timer.cleanUp();
        };
    }, []);

    const location = useLocation();

    useEffect(() => {
        const matched = AppRoutes.find((route) =>
            matchPath({ path: route.path, end: !!route.exact }, location.pathname)
        );
        const title = matched?.titleKey ? t(matched.titleKey) : '';
        document.title = title ? `${title} | Spara` : 'Spara';
    }, [location.pathname, t]);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // effect instead of componentDidMount, componentDidUpdate and componentWillUnmount
    useEffect(() => {
        if (navigator.platform.indexOf('Win') > -1 && mainPanel.current) {
            ps = new PerfectScrollbar(mainPanel.current, {
                suppressScrollX: true,
                suppressScrollY: false,
            });
            document.body.style.overflow = 'hidden';
        }
        window.addEventListener('resize', resizeFunction);

        // Specify how to clean up after this effect:
        return function cleanup() {
            if (navigator.platform.indexOf('Win') > -1 && ps) {
                ps.destroy();
            }
            window.removeEventListener('resize', resizeFunction);
        };
    }, []);
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };
    const sidebarMinimize = () => {
        const next = !miniActive;
        setMiniActive(next);
        userCollapsedRef.current = next;
    };
    const resizeFunction = () => {
        if (window.innerWidth >= 900) {
            setMobileOpen(false);
            if (!userCollapsedRef.current) {
                setMiniActive(false);
            }
        } else if (window.innerWidth >= 600) {
            setMobileOpen(false);
            setMiniActive(true);
        } else {
            setMiniActive(false);
        }
    };
    const getActiveRoute = (routes) => {
        let activeRoute = '';
        for (let i = 0; i < routes.length; i++) {
            if (routes[i].collapse) {
                let collapseActiveRoute = getActiveRoute(routes[i].views);
                if (collapseActiveRoute !== activeRoute) {
                    return collapseActiveRoute;
                }
            } else {
                if (window.location.href.indexOf(routes[i].path) !== -1) {
                    return routes[i].name;
                }
            }
        }
        return activeRoute;
    };

    return (
        <ErrorBoundary FallbackComponent={ServerError}>
            <div className={`${classes.wrapper} ${justLoggedIn ? layoutStyles.fadeIn : ''}`}>
                <Sidebar
                    routes={menus()}
                    handleDrawerToggle={handleDrawerToggle}
                    open={mobileOpen}
                    color={'red'}
                    bgColor={'black'}
                    miniActive={miniActive}
                    sidebarMinimize={sidebarMinimize}
                    loggingOut={loggingOut}
                    onLogoutStart={handleLogoutStart}
                    {...rest}
                />
                <div
                    className={mainPanelClasses}
                    style={loggingOut ? { animation: 'sparaSlideOutDown 0.4s ease-in forwards', pointerEvents: 'none' } : undefined}
                    ref={mainPanel}
                >
                    <AdminNavbar
                        sidebarMinimize={() => sidebarMinimize()}
                        miniActive={miniActive}
                        brandText={getActiveRoute(menus())}
                        handleDrawerToggle={handleDrawerToggle}
                        {...rest}
                    />

                    <div className={classes.content}>
                        <div className={classes.container}>
                            <PageTransition />
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
}
